import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';

export type PlaybackFormat = 'hls' | 'mp4';

type SecureVideoProps = Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'src' | 'ref'> & {
  format: PlaybackFormat;
  src: string | null;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
};

function canPlayHlsNatively(): boolean {
  const probe = document.createElement('video');
  return probe.canPlayType('application/vnd.apple.mpegurl') !== '';
}

const SecureVideo: React.FC<SecureVideoProps> = ({ format, src, videoRef, ...videoProps }) => {
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const destroyHls = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };

    const clearMedia = () => {
      destroyHls();
      video.removeAttribute('src');
      video.load();
    };

    if (!src) {
      clearMedia();
      return destroyHls;
    }

    if (format === 'mp4') {
      destroyHls();
      video.src = src;
      return () => {
        video.removeAttribute('src');
      };
    }

    if (canPlayHlsNatively()) {
      destroyHls();
      video.src = src;
      return () => {
        video.removeAttribute('src');
      };
    }

    if (Hls.isSupported()) {
      destroyHls();
      const hls = new Hls();
      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.loadSource(src);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          video.dispatchEvent(new Event('error'));
        }
      });
      return () => {
        destroyHls();
        video.removeAttribute('src');
      };
    }

    destroyHls();
    video.src = src;
    return () => {
      video.removeAttribute('src');
    };
  }, [format, src, videoRef]);

  return <video ref={videoRef} {...videoProps} />;
};

export default SecureVideo;
